import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";
import hljs from "../vendor/js/hljs-setup";
import { useEffect } from "react";


export default function RailsRedisSidekiq() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "Rails, Redis & Sidekiq: Scaling Notifications",
        "Jan. 30, 2026",
        "6min. read",
    ];

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title">{postTitle}</h1>

            <p className="lead">
                In modern web applications, user experience is king. Blocking a user's request while waiting for a third-party notification service (like sending an email or a push notification) is an anti-pattern. This post explores how to leverage Redis and Sidekiq in a Ruby on Rails environment to handle background jobs efficiently.
            </p>

            <h2 className="post-sect">The Architecture</h2>
            <p>
                The setup consists of three primary components:
            </p>
            <ul>
                <li><strong>Rails:</strong> The application logic that generates the work.</li>
                <li><strong>Redis:</strong> An in-memory data structure store used as a fast message queue.</li>
                <li><strong>Sidekiq:</strong> A background processing framework for Ruby that pulls jobs from Redis and executes them in separate threads.</li>
            </ul>

            <h2 className="post-sect">Implementation: Notification Queuing</h2>
            <p>
                When a user performs an action (e.g., placing an order), we want to return a response immediately and queue the notification in the background.
            </p>

            <h3 className="post-sect-sub">1. Define the Worker</h3>
            <p>
                Sidekiq uses "Workers" or "Jobs" to define the logic to be executed.
            </p>
            <pre>
                <code className="language-ruby">
                    {`# app/sidekiq/notification_worker.rb
class NotificationWorker
  include Sidekiq::Worker
  sidekiq_options retry: 3, queue: 'notifications'

  def perform(user_id, message)
    user = User.find(user_id)
    # Logic to send push notification or email
    PushNotificationService.send(user.device_token, message)
  end
end`}
                </code>
            </pre>

            <h3 className="post-sect-sub">2. Enqueue the Job</h3>
            <p>
                Instead of calling the service directly in your controller, you enqueue it.
            </p>
            <pre>
                <code className="language-ruby">
                    {`# app/controllers/orders_controller.rb
def create
  @order = Order.create(order_params)
  if @order.save
    # Enqueue notification for background processing
    NotificationWorker.perform_async(current_user.id, "Your order has been placed!")
    render json: @order, status: :created
  end
end`}
                </code>
            </pre>

            <h2 className="post-sect">Redis Configuration</h2>
            <p>
                Sidekiq needs to know where Redis is located. Usually configured in <code>config/initializers/sidekiq.rb</code>:
            </p>
            <pre>
                <code className="language-ruby">
                    {`Sidekiq.configure_server do |config|
  config.redis = { url: ENV['REDIS_URL'] || 'redis://localhost:6379/0' }
end

Sidekiq.configure_client do |config|
  config.redis = { url: ENV['REDIS_URL'] || 'redis://localhost:6379/0' }
end`}
                </code>
            </pre>

            <h2 className="post-sect">Monitoring with Sidekiq Web UI</h2>
            <p>
                One of Sidekiq's most powerful features is its real-time dashboard. You can monitor processed, failed, and busy jobs by mounting it in your <code>routes.rb</code>:
            </p>
            <pre>
                <code className="language-ruby">
                    {`# config/routes.rb
require 'sidekiq/web'
mount Sidekiq::Web => '/sidekiq'`}
                </code>
            </pre>

            <h2 className="post-sect">Summary</h2>
            <p>
                Moving heavy tasks like notifications out of the request-response cycle dramatically improves perceived performance. Redis provides the speed, and Sidekiq provides the management framework to make Rails applications scale effectively.
            </p>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}
